/**
 * Reusable cookie storage helper for persisting UI state.
 * Provides a simple API for saving and loading JSON data to cookies.
 */

const DEFAULT_EXPIRY_DAYS = 365

/**
 * Save a value to a cookie
 * @param {string} name - Cookie name
 * @param {any} value - Value to store (will be JSON stringified)
 * @param {number} expiryDays - Days until cookie expires (default: 365)
 */
export function saveToCookie(name, value, expiryDays = DEFAULT_EXPIRY_DAYS) {
  const cookieValue = JSON.stringify(value)
  const expiryDate = new Date()
  expiryDate.setDate(expiryDate.getDate() + expiryDays)
  document.cookie = `${name}=${encodeURIComponent(cookieValue)}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`
}

/**
 * Load a value from a cookie
 * @param {string} name - Cookie name
 * @param {any} defaultValue - Default value if cookie doesn't exist
 * @returns {any} Parsed cookie value or default
 */
export function loadFromCookie(name, defaultValue = null) {
  const cookies = document.cookie.split(';')

  for (const cookie of cookies) {
    const [cookieName, cookieValue] = cookie.trim().split('=')
    if (cookieName === name && cookieValue) {
      try {
        return JSON.parse(decodeURIComponent(cookieValue))
      } catch (e) {
        console.error(`Error parsing cookie "${name}":`, e)
        return defaultValue
      }
    }
  }

  return defaultValue
}

/**
 * Add an item to a Set stored in a cookie
 * @param {string} name - Cookie name
 * @param {string|number} item - Item to add
 * @param {number} expiryDays - Days until cookie expires
 */
export function addToCookieSet(name, item, expiryDays = DEFAULT_EXPIRY_DAYS) {
  const items = loadFromCookie(name, [])
  const set = new Set(items)
  set.add(item)
  saveToCookie(name, [...set], expiryDays)
}

/**
 * Remove an item from a Set stored in a cookie
 * @param {string} name - Cookie name
 * @param {string|number} item - Item to remove
 * @param {number} expiryDays - Days until cookie expires
 */
export function removeFromCookieSet(name, item, expiryDays = DEFAULT_EXPIRY_DAYS) {
  const items = loadFromCookie(name, [])
  const set = new Set(items)
  set.delete(item)
  saveToCookie(name, [...set], expiryDays)
}

/**
 * Check if an item exists in a Set stored in a cookie
 * @param {string} name - Cookie name
 * @param {string|number} item - Item to check
 * @returns {boolean}
 */
export function isInCookieSet(name, item) {
  const items = loadFromCookie(name, [])
  return items.includes(item)
}

/**
 * Toggle an item in a Set stored in a cookie
 * @param {string} name - Cookie name
 * @param {string|number} item - Item to toggle
 * @param {number} expiryDays - Days until cookie expires
 * @returns {boolean} New state (true if added, false if removed)
 */
export function toggleInCookieSet(name, item, expiryDays = DEFAULT_EXPIRY_DAYS) {
  const items = loadFromCookie(name, [])
  const set = new Set(items)

  if (set.has(item)) {
    set.delete(item)
    saveToCookie(name, [...set], expiryDays)
    return false
  } else {
    set.add(item)
    saveToCookie(name, [...set], expiryDays)
    return true
  }
}

